import * as SearchFilters from 'ha/types/SearchFilters';
import { CanonicalRegistration } from 'ha/constants/Filters';

import { TranslateFunc } from '@ha/intl';

type Value =
  | SearchFilters.TypeValue
  | SearchFilters.FurnitureValue
  | SearchFilters.BillsValue
  | SearchFilters.SuitableForValue
  | SearchFilters.RulesValue
  | SearchFilters.RegistrationValue
  | SearchFilters.GenderValue
  | SearchFilters.RecentlyAddedValue
  | SearchFilters.SearchAreaValue
  | SearchFilters.RadiusFilterValue
  | SearchFilters.FacilitiesValue
  | SearchFilters.AmenitiesValue;

interface Tag {
  category: keyof SearchFilters.Filters;
  value: Value;
  label: string;
}

export const getTagList = (
  T: TranslateFunc,
  filters: SearchFilters.Filters,
): Tag[] => {
  const tags: Tag[] = [
    {
      category: 'types',
      value: SearchFilters.TypeValue.APARTMENT,
      label: T('Apartment'),
    },
    {
      category: 'types',
      value: SearchFilters.TypeValue.PRIVATE_ROOM,
      label: T('Private Room'),
    },
    {
      category: 'types',
      value: SearchFilters.TypeValue.SHARED_ROOM,
      label: T('Shared Room'),
    },
    {
      category: 'types',
      value: SearchFilters.TypeValue.STUDIO,
      label: T('Studio'),
    },
    {
      category: 'furniture',
      value: SearchFilters.FurnitureValue.FURNISHED,
      label: T('Furnished'),
    },
    {
      category: 'furniture',
      value: SearchFilters.FurnitureValue.UNFURNISHED,
      label: T('Unfurnished'),
    },
    {
      category: 'bills',
      value: SearchFilters.BillsValue.INCLUDED,
      label: T('Bills Included'),
    },
    {
      category: 'bills',
      value: SearchFilters.BillsValue.EXCLUDED,
      label: T('Bills Excluded'),
    },
    {
      category: 'suitableFor',
      value: SearchFilters.SuitableForValue.STUDENTS,
      label: T('Suitable for students'),
    },
    {
      category: 'suitableFor',
      value: SearchFilters.SuitableForValue.WORKING_PROFESSIONALS,
      label: T('Suitable for working professionals'),
    },
    {
      category: 'suitableFor',
      value: SearchFilters.SuitableForValue.COUPLES,
      label: T('Suitable for couples'),
    },
    {
      category: 'rules',
      value: SearchFilters.RulesValue.SMOKING,
      label: T('Smoking Allowed'),
    },
    {
      category: 'rules',
      value: SearchFilters.RulesValue.PETS,
      label: T('Pets Allowed'),
    },
    {
      category: 'rules',
      value: SearchFilters.RulesValue.INSTRUMENT,
      label: T('Playing Instrument Allowed'),
    },
    {
      category: 'registration',
      value: CanonicalRegistration.Possible,
      label: T('Registration Possible'),
    },
    {
      category: 'registration',
      value: CanonicalRegistration.NotPossible,
      label: T('Registration Not Possible'),
    },
    {
      category: 'gender',
      value: SearchFilters.GenderValue.BOYS,
      label: T('Shared with Male'),
    },
    {
      category: 'gender',
      value: SearchFilters.GenderValue.GIRLS,
      label: T('Shared with Female'),
    },
    {
      category: 'recentlyAdded',
      value: '1-day',
      label: T('Last 24 hours'),
    },
    {
      category: 'recentlyAdded',
      value: '3-days',
      label: T('Last 3 days'),
    },
    {
      category: 'recentlyAdded',
      value: '7-days',
      label: T('Last 7 days'),
    },
    {
      category: 'recentlyAdded',
      value: '14-days',
      label: T('Last 14 days'),
    },
    {
      category: 'radius',
      value: SearchFilters.RadiusValue.R1,
      label: `${T('distance.within')} 1 km`,
    },
    {
      category: 'radius',
      value: SearchFilters.RadiusValue.R5,
      label: `${T('distance.within')} 5 km`,
    },
    {
      category: 'radius',
      value: SearchFilters.RadiusValue.R10,
      label: `${T('distance.within')} 10 km`,
    },
    {
      category: 'radius',
      value: SearchFilters.RadiusValue.R15,
      label: `${T('distance.within')} 15 km`,
    },
    {
      category: 'radius',
      value: SearchFilters.RadiusValue.R20,
      label: `${T('distance.within')} 20 km`,
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.PRIVATE_BATHROOM,
      label: T('private bathroom'),
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.BALCONY_TERRACE,
      label: T('Balcony/Terrace'),
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.GARDEN,
      label: T('Garden'),
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.KITCHEN,
      label: T('Kitchen'),
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.PET_FRIENDLY,
      label: T('listing.facilities.animal_allowed.yes'),
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.PARKING,
      label: T('Parking'),
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.WHEELCHAIR_ACCESSIBLE,
      label: T('Wheelchair accessible'),
    },
    {
      category: 'facilities',
      value: SearchFilters.FacilitiesValue.BASEMENT,
      label: T('Basement'),
    },
    {
      category: 'amenities',
      value: SearchFilters.AmenitiesValue.TV,
      label: T('TV'),
    },
    {
      category: 'amenities',
      value: SearchFilters.AmenitiesValue.DESK,
      label: T('Desk'),
    },
    {
      category: 'amenities',
      value: SearchFilters.AmenitiesValue.DISHWASHER,
      label: T('Dishwasher'),
    },
    {
      category: 'amenities',
      value: SearchFilters.AmenitiesValue.WASHING_MACHINE,
      label: T('Washing machine'),
    },
    {
      category: 'amenities',
      value: SearchFilters.AmenitiesValue.DRYER,
      label: T('Dryer'),
    },
    {
      category: 'amenities',
      value: SearchFilters.AmenitiesValue.AIR_CONDITIONING,
      label: T('Air conditioning'),
    },
    {
      category: 'amenities',
      value: SearchFilters.AmenitiesValue.HEATING,
      label: T('Heating'),
    },
  ];

  return tags.filter(
    ({ category, value }) =>
      filters[category] === value ||
      (Array.isArray(filters[category]) &&
        (filters[category] as Array<unknown>).includes(value)),
  );
};
